// src/components/accesFamille/AccesFamilleEleve.jsx
// ============================================================================
// Onglet « Accès famille » du dossier de l'élève
// ============================================================================
//
// Ce que l'école fait pour qu'un parent suive cet élève dans l'espace famille :
//
//   - remettre un code d'accès, pour cet élève et, s'il y a lieu, ses frères et
//     sœurs (même famille nombreuse ou même téléphone de parent) : le code est
//     affiché UNE fois, avec sa fiche PDF à imprimer ;
//   - voir les parents qui suivent l'élève, retirer un accès (motif exigé),
//     réinitialiser un mot de passe oublié (nouveau code), suspendre un compte ;
//   - annuler un code qui n'a pas servi.
//
// Le parent ne se déclare jamais tout seul : le téléphone et l'e-mail des
// fiches parents ne sont pas assez fiables pour l'identifier (voir
// backend2/services/EspaceFamilleService.js).
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  KeyRound, Printer, Copy, Loader2, UserX, RotateCcw, Ban, CheckCircle2, Users, Smartphone, Info, XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import ConfirmDialog from '../ConfirmDialog';
import { accesFamille } from './apiAccesFamille';

const BADGES_CODE = {
  valide: ['Valide', 'border-emerald-200 bg-emerald-50 text-emerald-800'],
  utilise: ['Utilisé', 'border-gray-200 bg-gray-50 text-gray-600'],
  expire: ['Expiré', 'border-amber-200 bg-amber-50 text-amber-800'],
  annule: ['Annulé', 'border-red-200 bg-red-50 text-red-700']
};

const dateCourte = (valeur) => {
  if (!valeur) return '-';
  const d = new Date(valeur);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const Badge = ({ classes, children }) => (
  <span className={`inline-flex h-6 w-[96px] items-center justify-center whitespace-nowrap rounded-lg border text-[11px] font-semibold ${classes}`}>
    {children}
  </span>
);

/** Le code tout juste émis : affiché une seule fois, avec sa fiche. */
const CodeEmis = ({ emis, onFermer, onToast }) => {
  const [impression, setImpression] = useState(false);

  const imprimer = async () => {
    setImpression(true);
    try {
      await accesFamille.fiche(emis.code);
    } catch (e) {
      onToast?.(e.message, 'error');
    } finally {
      setImpression(false);
    }
  };

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(emis.code);
      onToast?.('Code copié.', 'success');
    } catch {
      onToast?.('Copie impossible : notez le code à la main.', 'error');
    }
  };

  return (
    <div className="rounded-lg border-2 border-dashed border-[#246990]/40 bg-[#eef7fb] p-4">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[#246990]">
        {emis.type === 'reinitialisation' ? 'Code de réinitialisation' : "Code d'accès"} - à remettre au parent
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-4">
        <span className="font-mono text-3xl font-semibold tracking-[0.2em] text-gray-900">{emis.code}</span>
        <div className="flex flex-wrap gap-2">
          <Button onClick={imprimer} disabled={impression} className="h-9 gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]">
            {impression ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            Fiche à imprimer
          </Button>
          <Button variant="outline" onClick={copier} className="h-9 gap-2">
            <Copy className="h-4 w-4" />
            Copier
          </Button>
        </div>
      </div>
      <p className="mt-2 text-xs text-gray-600">
        Valable une seule fois, jusqu'au {dateCourte(emis.expire_le)}.
        {emis.eleves?.length ? ` Élève(s) : ${emis.eleves.join(', ')}.` : ''}
        {' '}Ce code ne sera plus jamais affiché : imprimez la fiche ou notez-le maintenant.
      </p>
      <button type="button" onClick={onFermer} className="mt-2 text-xs font-semibold text-[#246990] hover:underline">
        C'est fait, masquer le code
      </button>
    </div>
  );
};

const AccesFamilleEleve = ({ eleveId, onToast }) => {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [fratrieChoisie, setFratrieChoisie] = useState([]);
  const [destinataire, setDestinataire] = useState('');
  const [enCours, setEnCours] = useState(null);
  const [emis, setEmis] = useState(null);
  const [action, setAction] = useState(null); // { type: 'revoquer'|'suspendre'|'annuler', cible }
  const [motif, setMotif] = useState('');

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const reponse = await accesFamille.eleve(eleveId);
      setDonnees(reponse.data);
    } catch (e) {
      setErreur(e.message);
    }
  }, [eleveId]);

  useEffect(() => { charger(); }, [charger]);

  const executer = async (cle, travail) => {
    setEnCours(cle);
    try {
      await travail();
    } catch (e) {
      onToast?.(e.message, 'error');
    } finally {
      setEnCours(null);
    }
  };

  const creerCode = () => executer('code', async () => {
    const reponse = await accesFamille.creerCode(eleveId, { eleve_ids: fratrieChoisie, destinataire_nom: destinataire });
    setEmis({ ...reponse.data, type: 'activation' });
    setFratrieChoisie([]);
    setDestinataire('');
    await charger();
  });

  const reinitialiser = (compte) => executer(`reinit-${compte.compte_id}`, async () => {
    const reponse = await accesFamille.reinitialiser(compte.compte_id);
    setEmis({ ...reponse.data, type: 'reinitialisation' });
    await charger();
  });

  const reactiver = (compte) => executer(`statut-${compte.compte_id}`, async () => {
    const reponse = await accesFamille.statutCompte(compte.compte_id, 'actif');
    onToast?.(reponse.message, 'success');
    await charger();
  });

  const confirmerAction = async () => {
    if (!action) return;
    try {
      let reponse;
      if (action.type === 'revoquer') reponse = await accesFamille.revoquer(action.cible.lien_id, motif);
      if (action.type === 'suspendre') reponse = await accesFamille.statutCompte(action.cible.compte_id, 'suspendu', motif);
      if (action.type === 'annuler') reponse = await accesFamille.annulerCode(action.cible.id);
      onToast?.(reponse?.message || 'Fait.', 'success');
      setAction(null);
      setMotif('');
      await charger();
    } catch (e) {
      onToast?.(e.message, 'error');
    }
  };

  if (erreur) {
    return (
      <div role="alert" className="m-5 flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <span className="min-w-0 flex-1">{erreur}</span>
        {/* Un bouton borde, pas un lien nu (decision du 07/10/2026). */}
        <button
          type="button"
          onClick={charger}
          className="h-8 shrink-0 rounded-lg border border-red-300 px-3 text-xs font-semibold transition-colors hover:bg-red-100"
        >
          Réessayer
        </button>
      </div>
    );
  }
  if (!donnees) {
    return (
      <div className="flex items-center justify-center gap-2 p-10 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin text-[#246990]" />
        Chargement des accès famille…
      </div>
    );
  }

  const peutGerer = donnees.droits?.gerer;
  const actifs = donnees.comptes.filter((c) => !c.revoque_le);
  const retires = donnees.comptes.filter((c) => c.revoque_le);

  return (
    <div className="grid gap-5 p-5 pb-10 lg:grid-cols-2">
      <div className="space-y-4">
        <div className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] p-3 text-sm text-gray-700">
          <p className="flex items-center gap-2 font-semibold text-[#246990]"><Info className="h-4 w-4" />Espace famille</p>
          <p className="mt-1 text-xs">
            Remettez un code au parent, en main propre. Il crée son espace avec son téléphone et un mot de passe,
            puis suit notes, absences, emploi du temps et paiements de ses enfants.
          </p>
        </div>

        {emis && <CodeEmis emis={emis} onFermer={() => setEmis(null)} onToast={onToast} />}

        {peutGerer ? (
          <section className="rounded-lg border p-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <KeyRound className="h-4 w-4 text-[#246990]" />
              Remettre un code d'accès
            </h3>

            <label className="mt-3 block text-sm text-gray-700">
              Remis à (facultatif)
              <input
                className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-[#246990]"
                value={destinataire}
                onChange={(e) => setDestinataire(e.target.value)}
                list={`parents-${eleveId}`}
                placeholder="Nom du parent"
                maxLength={150}
              />
              <datalist id={`parents-${eleveId}`}>
                {donnees.parents.map((p) => <option key={`${p.nom_prenom}-${p.telephone}`} value={p.nom_prenom} />)}
              </datalist>
            </label>

            {donnees.fratrie.length > 0 && (
              <div className="mt-3">
                <p className="flex items-center gap-2 text-sm text-gray-700"><Users className="h-4 w-4 text-gray-400" />Même code pour la fratrie</p>
                <p className="text-[11px] text-gray-500">Élèves de la même famille nombreuse ou avec le même téléphone de parent.</p>
                <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
                  {donnees.fratrie.map((f) => (
                    <label key={f.id} className="flex cursor-pointer items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm hover:bg-gray-50">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#246990]"
                        checked={fratrieChoisie.includes(f.id)}
                        onChange={(e) => setFratrieChoisie((liste) => (e.target.checked ? [...liste, f.id] : liste.filter((id) => id !== f.id)))}
                      />
                      <span className="truncate">{f.prenom} {f.nom}</span>
                      {f.classe_nom && <span className="ml-auto shrink-0 text-xs text-gray-400">{f.classe_nom}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <Button onClick={creerCode} disabled={enCours === 'code'} className="mt-4 h-9 gap-2 bg-[#246990] text-white hover:bg-[#1f5a7a]">
              {enCours === 'code' ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Générer le code
            </Button>
          </section>
        ) : (
          <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            Vous pouvez consulter les accès, mais pas les modifier : il faut la permission « Espace famille : gérer les accès ».
          </p>
        )}

        {donnees.parents.length > 0 && (
          <section className="rounded-lg border p-4">
            <h3 className="text-sm font-semibold text-gray-900">Parents au dossier</h3>
            <ul className="mt-2 space-y-1 text-sm text-gray-600">
              {donnees.parents.map((p) => (
                <li key={`${p.nom_prenom}-${p.telephone}`}>
                  {p.nom_prenom}{p.lien_parente ? ` (${p.lien_parente})` : ''}
                  <span className="text-gray-400"> · {p.telephone || 'sans téléphone'}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <div className="space-y-4">
        <section className="rounded-lg border p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <Smartphone className="h-4 w-4 text-[#246990]" />
            Parents qui suivent l'élève
          </h3>
          {actifs.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">Aucun parent n'a encore activé son espace pour cet élève.</p>
          ) : (
            <ul className="mt-2 divide-y">
              {actifs.map((c) => (
                <li key={c.lien_id} className="py-2.5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{c.nom_affiche || 'Parent'} <span className="font-normal text-gray-500">· {c.telephone}</span></p>
                      <p className="text-[11px] text-gray-500">
                        Depuis le {dateCourte(c.rattache_le)}
                        {c.derniere_connexion ? ` · dernière connexion le ${dateCourte(c.derniere_connexion)}` : ''}
                        {c.nombre_enfants > 1 ? ` · ${c.nombre_enfants} enfants suivis` : ''}
                      </p>
                    </div>
                    {c.statut === 'suspendu' && <Badge classes="border-red-200 bg-red-50 text-red-700">Suspendu</Badge>}
                  </div>
                  {peutGerer && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" className="gap-1.5" disabled={enCours === `reinit-${c.compte_id}`} onClick={() => reinitialiser(c)}>
                        {enCours === `reinit-${c.compte_id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                        Mot de passe oublié
                      </Button>
                      {c.statut === 'suspendu' ? (
                        <Button size="sm" variant="outline" className="gap-1.5" disabled={enCours === `statut-${c.compte_id}`} onClick={() => reactiver(c)}>
                          <CheckCircle2 className="h-3.5 w-3.5" />
                          Réactiver le compte
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setMotif(''); setAction({ type: 'suspendre', cible: c }); }}>
                          <Ban className="h-3.5 w-3.5" />
                          Suspendre le compte
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="gap-1.5 text-red-700" onClick={() => { setMotif(''); setAction({ type: 'revoquer', cible: c }); }}>
                        <UserX className="h-3.5 w-3.5" />
                        Retirer l'accès à cet élève
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
          {retires.length > 0 && (
            <details className="mt-3 text-xs text-gray-500">
              <summary className="cursor-pointer font-semibold">Accès retirés ({retires.length})</summary>
              <ul className="mt-1 space-y-1">
                {retires.map((c) => (
                  <li key={c.lien_id}>{c.nom_affiche || 'Parent'} · {c.telephone} - retiré le {dateCourte(c.revoque_le)}{c.motif_revocation ? ` : ${c.motif_revocation}` : ''}</li>
                ))}
              </ul>
            </details>
          )}
        </section>

        <section className="rounded-lg border p-4">
          <h3 className="text-sm font-semibold text-gray-900">Codes émis</h3>
          {donnees.codes.length === 0 ? (
            <p className="mt-2 text-sm text-gray-500">Aucun code émis pour cet élève.</p>
          ) : (
            <ul className="mt-2 divide-y">
              {donnees.codes.map((k) => {
                const [libelle, classes] = BADGES_CODE[k.statut];
                return (
                  <li key={k.id} className="flex flex-wrap items-center gap-3 py-2">
                    <Badge classes={classes}>{libelle}</Badge>
                    <div className="min-w-0 flex-1 text-sm">
                      <p className="text-gray-800">
                        <span className="font-mono">••••-{k.fin}</span>
                        <span className="text-gray-500"> · {k.type === 'reinitialisation' ? 'réinitialisation' : 'accès'}{k.destinataire_nom ? ` · ${k.destinataire_nom}` : ''}</span>
                      </p>
                      <p className="text-[11px] text-gray-500">
                        Émis le {dateCourte(k.cree_le)}{k.cree_par ? ` par ${k.cree_par}` : ''}
                        {k.statut === 'utilise' ? ` · utilisé le ${dateCourte(k.utilise_le)}` : ` · expire le ${dateCourte(k.expire_le)}`}
                      </p>
                    </div>
                    {peutGerer && k.statut === 'valide' && (
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setAction({ type: 'annuler', cible: k })}>
                        <XCircle className="h-3.5 w-3.5" />
                        Annuler
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <ConfirmDialog
        isOpen={Boolean(action)}
        onClose={() => { setAction(null); setMotif(''); }}
        onConfirm={confirmerAction}
        type={action?.type === 'annuler' ? 'warning' : 'error'}
        title={{
          revoquer: "Retirer l'accès à cet élève",
          suspendre: 'Suspendre le compte famille',
          annuler: 'Annuler ce code'
        }[action?.type] || ''}
        description={{
          revoquer: "Le parent ne verra plus cet élève dans son espace. Ses autres enfants restent visibles. Un nouveau code sera nécessaire pour rétablir l'accès.",
          suspendre: "Le parent ne pourra plus se connecter à l'espace famille, pour aucun de ses enfants, jusqu'à la réactivation.",
          annuler: 'Le code ne pourra plus être utilisé. Le parent devra en recevoir un nouveau.'
        }[action?.type] || ''}
        confirmText={action?.type === 'annuler' ? 'Annuler le code' : 'Confirmer'}
        cancelText="Retour"
        confirmDisabled={action?.type !== 'annuler' && motif.trim().length < 5}
      >
        {action && action.type !== 'annuler' && (
          <label className="block text-sm text-gray-700">
            Motif (obligatoire)
            <textarea
              className="mt-1 w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#246990]"
              rows={3}
              maxLength={255}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Cinq caractères au moins"
            />
          </label>
        )}
      </ConfirmDialog>
    </div>
  );
};

export default AccesFamilleEleve;
